import React, { useEffect, useRef, useState } from "react";
import { Avatar, Box, Typography, TextField, IconButton, Button, Stack, Paper, Chip } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import DoneIcon from "@mui/icons-material/Done";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ReplayIcon from "@mui/icons-material/Replay";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ImageZoom from "react-medium-image-zoom";
import LinkPreview, { extractUrls } from "../LinkPreview";
import Global from "../../customlib/Global";
import { getAvatarColor, getInitials, getLabelColor } from "./livechatDisplayUtils";
import { PASTEL } from "./livechatTheme";

export default function ChatPanel({ session, messages, onSendMessage, onSendPhoto, onRetryMessage, onCloseSession, onOpenLabelDialog, isMobile, onBack }) {
    const [text, setText] = useState("");
    const [pendingPhoto, setPendingPhoto] = useState(null);
    const [photoCaption, setPhotoCaption] = useState("");
    const bottomRef = useRef(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    if (!session) {
        return (
            <Box sx={{
                flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                gap: 1, backgroundColor: PASTEL.background,
            }}>
                <ChatBubbleOutlineIcon sx={{ fontSize: 56, color: "text.disabled" }} />
                <Typography color="text.secondary">Pilih percakapan di sebelah kiri untuk mulai membalas</Typography>
            </Box>
        );
    }

    const handleSend = () => {
        if (!text.trim()) return;
        onSendMessage(text.trim());
        setText("");
    };

    const handlePickPhoto = (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (pendingPhoto) URL.revokeObjectURL(pendingPhoto.previewUrl);
        setPendingPhoto({ file, previewUrl: URL.createObjectURL(file) });
        setPhotoCaption("");
    };

    const handleCancelPhoto = () => {
        if (!pendingPhoto) return;
        URL.revokeObjectURL(pendingPhoto.previewUrl);
        setPendingPhoto(null);
        setPhotoCaption("");
    };

    // Kompresi + upload + kirim sekarang sepenuhnya jadi tanggung jawab onSendPhoto (index.jsx),
    // yang membuat bubble optimistic "sending" di daftar pesan utama begitu dipanggil — supaya
    // kegagalan (kompresi/upload/kirim) tampil sebagai status "Gagal terkirim" + tombol retry di
    // bubble itu sendiri, bukan lewat sendingPhoto/try-catch lokal di panel komposer ini lagi.
    const handleSubmitPhoto = () => {
        if (!pendingPhoto) return;
        const { file, previewUrl } = pendingPhoto;
        const caption = photoCaption.trim();
        setPendingPhoto(null);
        setPhotoCaption("");
        onSendPhoto(file, caption, previewUrl);
    };

    const labelColor = getLabelColor(session.label?.labelType);
    const isActive = session.status === "active";

    return (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
            <Box sx={{
                px: { xs: 1.5, sm: 2.5 }, py: 1.5, borderBottom: `1px solid ${PASTEL.border}`, backgroundColor: PASTEL.surface,
                display: "flex", justifyContent: "space-between", alignItems: "center", gap: { xs: 1, sm: 2 },
            }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                    {isMobile && (
                        <IconButton size="small" onClick={onBack} sx={{ flexShrink: 0, ml: -0.5 }}>
                            <ArrowBackIcon fontSize="small" />
                        </IconButton>
                    )}
                    <Avatar sx={{ bgcolor: getAvatarColor(session.customerName), width: 40, height: 40, fontSize: 15, flexShrink: 0 }}>
                        {getInitials(session.customerName)}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2} noWrap>
                                {session.customerName}
                            </Typography>
                            {session.label?.labelType && (
                                <Chip
                                    size="small"
                                    label={session.label.labelType}
                                    sx={{
                                        height: 20, fontSize: 11, flexShrink: 0,
                                        backgroundColor: labelColor ? `${labelColor}22` : undefined,
                                        color: labelColor || undefined,
                                        border: labelColor ? `1px solid ${labelColor}55` : undefined,
                                    }}
                                />
                            )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary" noWrap>
                            {session.customerPhone} · {session.brand}
                        </Typography>
                    </Box>
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                    {!isMobile && (
                        <Chip
                            size="small"
                            label={isActive ? "Aktif" : "Selesai"}
                            variant={isActive ? "filled" : "outlined"}
                            sx={isActive
                                ? { fontWeight: 600, backgroundColor: PASTEL.primary, color: "#fff" }
                                : { fontWeight: 600 }}
                        />
                    )}
                    <Button
                        size="small" variant="outlined" onClick={onOpenLabelDialog}
                        startIcon={<LabelOutlinedIcon fontSize="small" />}
                        sx={{
                            textTransform: "none",
                            minWidth: 0,
                            px: isMobile ? 1 : 2,
                            color: PASTEL.primary,
                            borderColor: PASTEL.primary,
                            "&:hover": { borderColor: PASTEL.primaryDark, backgroundColor: PASTEL.primarySoft },
                            "& .MuiButton-startIcon": { mr: isMobile ? 0 : 1 },
                        }}
                    >
                        {!isMobile && "Set Label"}
                    </Button>
                    {isActive && (
                        <Button
                            size="small" variant="contained" onClick={onCloseSession}
                            startIcon={<CheckCircleOutlineIcon fontSize="small" />}
                            sx={{
                                textTransform: "none",
                                minWidth: 0,
                                px: isMobile ? 1 : 2,
                                backgroundColor: PASTEL.primary,
                                "&:hover": { backgroundColor: PASTEL.primaryDark },
                                "& .MuiButton-startIcon": { mr: isMobile ? 0 : 1 },
                            }}
                        >
                            {!isMobile && "Selesai"}
                        </Button>
                    )}
                </Stack>
            </Box>

            <Box sx={{ flex: 1, overflowY: "auto", p: { xs: 1.5, sm: 2 }, backgroundColor: PASTEL.background }}>
                {messages.map((m) => {
                    const isAgent = m.sender === "agent";
                    const urls = extractUrls(m.text);
                    return (
                        <Box key={m._id} sx={{ display: "flex", justifyContent: isAgent ? "flex-end" : "flex-start", mb: 1 }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 0.7, maxWidth: { xs: "85%", sm: "70%" },
                                    backgroundColor: isAgent ? PASTEL.primary : PASTEL.surface,
                                    color: isAgent ? "#fff" : "text.primary",
                                    borderRadius: 2,
                                    borderBottomRightRadius: isAgent ? 4 : 16,
                                    borderBottomLeftRadius: isAgent ? 16 : 4,
                                    boxShadow: "0 1px 1px rgba(0,0,0,0.08)",
                                }}
                            >
                                {m.attachmentUrl && (
                                    <Box sx={{ mb: 0.5, lineHeight: 0 }}>
                                        <ImageZoom
                                            image={{
                                                src: m.attachmentUrl,
                                                alt: "lampiran",
                                                style: { maxWidth: "100%", borderRadius: 8, display: "block" },
                                            }}
                                            zoomImage={{ src: m.attachmentUrl, alt: "lampiran" }}
                                        />
                                    </Box>
                                )}
                                {m.text && <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{m.text}</Typography>}
                                {urls.length > 0 && <LinkPreview key={urls[0]} url={urls[0]} />}
                                <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center" sx={{ mt: 0.5 }}>
                                    <Typography variant="caption" sx={{ color: isAgent ? "rgba(255,255,255,0.75)" : "text.secondary" }}>
                                        {Global.formatDateTimeToStrTime24(m.created_at, true)}
                                    </Typography>
                                    {isAgent && (
                                        m.deliveryStatus === "failed" ? (
                                            <>
                                                <ErrorOutlineIcon sx={{ fontSize: 13, color: "#ffd2e0" }} />
                                                <Typography variant="caption" sx={{ fontSize: 11, color: "#ffd2e0" }}>Gagal terkirim</Typography>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => onRetryMessage(m)}
                                                    aria-label="Kirim ulang"
                                                    sx={{ p: 0.25, color: "#ffd2e0", "&:hover": { bgcolor: "rgba(255,255,255,0.15)" } }}
                                                >
                                                    <ReplayIcon sx={{ fontSize: 14 }} />
                                                </IconButton>
                                            </>
                                        ) : m.deliveryStatus === "sending" ? (
                                            <AccessTimeIcon sx={{ fontSize: 13, color: "rgba(255,255,255,0.75)" }} />
                                        ) : m.readByCustomerAt ? (
                                            // Biru (#8ecbff) menandai "sudah dibaca", konsisten dengan centang di
                                            // livechat-customer-fe/ChatScreen.jsx — bukan cuma beda bentuk ikon.
                                            <DoneAllIcon sx={{ fontSize: 14, color: "#8ecbff" }} />
                                        ) : (
                                            <DoneIcon sx={{ fontSize: 14, color: "rgba(255,255,255,0.75)" }} />
                                        )
                                    )}
                                </Stack>
                            </Paper>
                        </Box>
                    );
                })}
                <div ref={bottomRef} />
            </Box>

            <input type="file" accept="image/*" hidden ref={fileInputRef} onChange={handlePickPhoto} />

            {pendingPhoto ? (
                <Box sx={{
                    px: { xs: 1, sm: 2 }, py: 1.5, borderTop: `1px solid ${PASTEL.border}`, backgroundColor: PASTEL.surfaceAlt,
                }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", letterSpacing: 0.2 }}>
                            Pratinjau foto
                        </Typography>
                        <IconButton size="small" onClick={handleCancelPhoto}>
                            <CloseRoundedIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box
                            component="img"
                            src={pendingPhoto.previewUrl}
                            alt="preview"
                            sx={{
                                width: 56, height: 56, borderRadius: 1.5, objectFit: "cover",
                                flexShrink: 0, border: `1px solid ${PASTEL.border}`, backgroundColor: PASTEL.surface,
                            }}
                        />
                        <TextField
                            fullWidth size="small" placeholder="Tambahkan keterangan (opsional)..."
                            value={photoCaption}
                            onChange={(e) => setPhotoCaption(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSubmitPhoto(); } }}
                            InputProps={{ sx: { borderRadius: 3, backgroundColor: PASTEL.surface } }}
                        />
                        <IconButton
                            onClick={handleSubmitPhoto}
                            sx={{
                                flexShrink: 0, width: 40, height: 40,
                                backgroundColor: PASTEL.primary, color: "#fff",
                                "&:hover": { backgroundColor: PASTEL.primaryDark },
                            }}
                        >
                            <SendIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>
            ) : (
                <Box sx={{
                    px: { xs: 1, sm: 2 }, py: 1.5, borderTop: `1px solid ${PASTEL.border}`, backgroundColor: PASTEL.surface,
                    display: "flex", alignItems: "flex-end", gap: 1, flexShrink: 0,
                }}>
                    <IconButton onClick={() => fileInputRef.current?.click()} sx={{ color: "text.secondary", flexShrink: 0 }}>
                        <AttachFileIcon />
                    </IconButton>
                    <TextField
                        fullWidth size="small" placeholder="Tulis balasan..."
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                        multiline
                        maxRows={4}
                        InputProps={{ sx: { borderRadius: 3, backgroundColor: PASTEL.surfaceAlt, py: 1 } }}
                        sx={{ "& fieldset": { border: "none" } }}
                    />
                    <IconButton
                        onClick={handleSend}
                        sx={{
                            flexShrink: 0,
                            width: 40, height: 40,
                            backgroundColor: PASTEL.primary,
                            color: "#fff",
                            "&:hover": { backgroundColor: PASTEL.primaryDark },
                        }}
                    >
                        <SendIcon fontSize="small" />
                    </IconButton>
                </Box>
            )}
        </Box>
    );
}
